import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Edit, Save, X, EyeOff, RotateCcw } from 'lucide-react';

/**
 * Manage the size vocabulary (#208).
 *
 * Migration 060 shipped this vocabulary read-only, seeded with 24 sizes, while
 * its own model docstring promised that "a genuinely new size is added here by
 * an admin". That escape hatch was never built — so a 6oz cortado meant
 * hand-writing SQL against production. This screen is that missing half.
 *
 * Sizes are shared: `menu_item_prices` prices them per restaurant, and #209
 * will let a Food declare which ones it typically comes in. Editing here
 * therefore reaches every menu item using the size, which is why renaming is
 * offered and deleting is not.
 *
 * `api` arrives as a prop rather than being imported. It lives inside App.js
 * alongside everything else, and this repo's CLAUDE.md is explicit that
 * component extraction should be a deliberate refactor rather than a drive-by
 * while shipping a feature — so the extraction that would let this import it
 * directly is left to #210.
 */

// Sizes are DISCRETE options; price units are CONTINUOUS measures. That is the
// whole rule, and it is why `1 lb` is a size while `lb` is a unit — and why
// both are correct. Shown in the UI because the distinction is the one thing
// about this screen that isn't self-evident.
const SIZE_VS_UNIT_HELP =
  'A size is a discrete choice the diner picks — Small, 16 oz, Family. ' +
  'A price unit is a continuous measure the dish is sold by — $18.99/lb. ' +
  'Brisket weighed to order uses the unit; a 1 lb tub is a size.';

const SizeManager = ({ api }) => {
  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showRetired, setShowRetired] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [newSize, setNewSize] = useState({ display_name: '', sort_order: '' });
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ display_name: '', sort_order: '' });

  const fetchSizes = useCallback(async () => {
    try {
      setLoading(true);
      // Always fetch the full list including retired ones. The toggle filters
      // client-side so flipping it doesn't cost a round trip, and so a retired
      // size can't briefly vanish from the table mid-edit.
      const data = await api.get('/api/v1/sizes/?include_inactive=true');
      setSizes(data);
    } catch (err) {
      setError(err.detail || 'Failed to fetch sizes');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchSizes();
  }, [fetchSizes]);

  const handleCreate = async () => {
    if (!newSize.display_name.trim()) {
      setError('Give the size a name');
      return;
    }
    try {
      const payload = {
        display_name: newSize.display_name,
        // Default new sizes to the end of the list. The seeded values leave
        // gaps of 10, so an explicit number can slot one between two existing
        // sizes without renumbering everything.
        sort_order: newSize.sort_order === '' ? 900 : Number(newSize.sort_order),
      };
      const created = await api.post('/api/v1/sizes/', payload);
      setSuccess(`Added "${created.display_name}"`);
      setNewSize({ display_name: '', sort_order: '' });
      setShowForm(false);
      fetchSizes();
    } catch (err) {
      // The backend's 400 explains itself — a duplicate says so, and says when
      // the clash is with a retired size. Surfacing the raw detail is more use
      // than a generic "failed to save".
      setError(err.detail || 'Failed to add the size');
    }
  };

  const startEdit = (size) => {
    setEditingId(size.id);
    setEditDraft({ display_name: size.display_name, sort_order: String(size.sort_order) });
  };

  const handleSaveEdit = async (size) => {
    try {
      await api.put(`/api/v1/sizes/${size.id}`, {
        display_name: editDraft.display_name,
        sort_order: Number(editDraft.sort_order),
      });
      setSuccess(`Updated "${editDraft.display_name}"`);
      setEditingId(null);
      fetchSizes();
    } catch (err) {
      setError(err.detail || 'Failed to update the size');
    }
  };

  const handleToggleActive = async (size) => {
    // Retire rather than delete: menu items are priced against these, and the
    // vocabulary becomes diner-facing once sizes are selectable when logging a
    // dish. Retiring hides it from every picker and leaves the prices intact.
    const retiring = size.is_active;
    if (retiring && !window.confirm(
      `Retire "${size.display_name}"?\n\n` +
      'It disappears from the size pickers, but any menu item already priced ' +
      'at this size keeps both its price and its label. You can bring it back ' +
      'at any time.'
    )) {
      return;
    }
    try {
      await api.put(`/api/v1/sizes/${size.id}`, { is_active: !size.is_active });
      setSuccess(retiring ? `Retired "${size.display_name}"` : `Restored "${size.display_name}"`);
      fetchSizes();
    } catch (err) {
      setError(err.detail || 'Failed to update the size');
    }
  };

  const visible = showRetired ? sizes : sizes.filter((s) => s.is_active);
  const retiredCount = sizes.filter((s) => !s.is_active).length;

  return (
    <div className="section">
      <div className="section-header">
        <h2>Sizes</h2>
        <button onClick={() => setShowForm(!showForm)} className="btn btn-primary">
          <Plus size={16} />
          Add Size
        </button>
      </div>

      <p className="section-description">{SIZE_VS_UNIT_HELP}</p>

      {error && (
        <div className="error-message">
          <div className="message-content">
            <p>{error}</p>
            <button onClick={() => setError('')} className="close-btn"><X size={16} /></button>
          </div>
        </div>
      )}
      {success && (
        <div className="success-message">
          <div className="message-content">
            <p>{success}</p>
            <button onClick={() => setSuccess('')} className="close-btn"><X size={16} /></button>
          </div>
        </div>
      )}

      {showForm && (
        <div className="form-container">
          <h3>Add New Size</h3>
          <div className="form">
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={newSize.display_name}
                placeholder="6 oz"
                onChange={(e) => setNewSize({ ...newSize, display_name: e.target.value })}
              />
              <small>Shown to diners exactly as typed.</small>
            </div>
            <div className="form-group">
              <label>Sort order</label>
              <input
                type="number"
                value={newSize.sort_order}
                placeholder="900"
                onChange={(e) => setNewSize({ ...newSize, sort_order: e.target.value })}
              />
              <small>Lower sorts first. Seeded sizes leave gaps of 10 so you can slot between them.</small>
            </div>
            <div className="form-actions">
              <button onClick={handleCreate} className="btn btn-primary">
                <Save size={16} />
                Save
              </button>
              <button onClick={() => setShowForm(false)} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {retiredCount > 0 && (
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={showRetired}
            onChange={(e) => setShowRetired(e.target.checked)}
          />
          Show retired sizes ({retiredCount})
        </label>
      )}

      {loading ? (
        <div className="loading-container"><div className="spinner"></div></div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              {/* The `slug` is deliberately not shown. It is an internal handle
                  — nothing reads it: this form and menu_item_prices both key on
                  size_id, and web/iOS/iPad have no size code at all. Showing it
                  only invited the question of why a hand-added "6 oz" rendered
                  as `6_oz` directly above the seeded `8oz`, which is a real
                  inconsistency with no consequence. The generator now collapses
                  that case anyway; the column earns nothing either way. */}
              <tr>
                <th>Name</th>
                <th>Sort order</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((size) => {
                const isEditing = editingId === size.id;
                return (
                  <tr key={size.id} className={size.is_active ? '' : 'row-inactive'}>
                    <td className="font-medium">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editDraft.display_name}
                          onChange={(e) => setEditDraft({ ...editDraft, display_name: e.target.value })}
                        />
                      ) : (
                        size.display_name
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <input
                          type="number"
                          value={editDraft.sort_order}
                          onChange={(e) => setEditDraft({ ...editDraft, sort_order: e.target.value })}
                        />
                      ) : (
                        size.sort_order
                      )}
                    </td>
                    <td>{size.is_active ? 'Active' : 'Retired'}</td>
                    <td className="actions">
                      {isEditing ? (
                        <>
                          <button onClick={() => handleSaveEdit(size)} className="btn-icon btn-edit" title="Save">
                            <Save size={16} />
                          </button>
                          <button onClick={() => setEditingId(null)} className="btn-icon" title="Cancel">
                            <X size={16} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(size)} className="btn-icon btn-edit" title="Rename or reorder">
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleToggleActive(size)}
                            className="btn-icon"
                            title={size.is_active ? 'Retire (hides it; prices are kept)' : 'Restore'}
                          >
                            {size.is_active ? <EyeOff size={16} /> : <RotateCcw size={16} />}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SizeManager;
